import type { SupportedSourceConnector } from "../../../../lib/corpusApi";

/** How confidently a URL will extract, per the manifest. "unmatched" — no connector's
 *  url_pattern matches; the backend has no generic/catch-all fallback (see
 *  design-documents/024), so submitting this URL will be rejected outright. "best_effort" —
 *  matches a connector, but that connector's coverage isn't guaranteed (e.g. a PubMed URL with
 *  no PMC open-access copy) — submission may still fail. "guaranteed" — matches a connector
 *  whose coverage is guaranteed; no warning needed. */
export type UrlSupportLevel = "unmatched" | "best_effort" | "guaranteed";

/** Classifies one URL against the manifest. Returns null while the manifest hasn't loaded yet
 *  (sourceConnectors === null) — silence, not a false warning, while we don't yet know what's
 *  supported. */
export function classifyUrlSupport(
  url: string,
  sourceConnectors: SupportedSourceConnector[] | null
): UrlSupportLevel | null {
  if (!sourceConnectors) return null;
  const match = sourceConnectors.find((c) => new RegExp(c.urlPattern).test(url));
  if (!match) return "unmatched";
  return match.coverage === "guaranteed" ? "guaranteed" : "best_effort";
}

/** Partitions `urls` by support level for the submit warning — unmatched URLs will be rejected
 *  outright, best-effort URLs may still fail. Guaranteed and not-yet-classifiable (manifest not
 *  loaded) URLs are omitted from both lists, since neither warrants a warning. */
export interface UrlSupportBreakdown {
  unmatched: string[];
  bestEffort: string[];
}

export function classifyUrls(
  urls: string[],
  sourceConnectors: SupportedSourceConnector[] | null
): UrlSupportBreakdown {
  const unmatched: string[] = [];
  const bestEffort: string[] = [];
  for (const url of urls) {
    const level = classifyUrlSupport(url, sourceConnectors);
    if (level === "unmatched") unmatched.push(url);
    else if (level === "best_effort") bestEffort.push(url);
  }
  return { unmatched, bestEffort };
}
